import { Box, CircularProgress } from '@mui/material';
import { DataGrid, type DataGridProps, type GridValidRowModel } from '@mui/x-data-grid';
import type { SxProps, Theme } from '@mui/material/styles';
import { dataGridSx, WAF_YELLOW } from '../theme';

/**
 * Branded loading overlay — a centred yellow Material spinner shown while the
 * grid is fetching, in place of MUI's default linear progress bar.
 */
function WafLoadingOverlay() {
  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <CircularProgress sx={{ color: WAF_YELLOW }} />
    </Box>
  );
}

/**
 * WafDataGrid — the standard table component for the WAF admin portal.
 *
 * A thin, fully-typed wrapper around MUI <DataGrid> that bakes in the house
 * style (`dataGridSx`: brand-yellow header, clean empty/no-rows surface, hover
 * rows, yellow footer) plus the shared defaults that were previously repeated
 * on every page (`density="compact"`, `disableRowSelectionOnClick`).
 *
 * Prefer this over <DataGrid> directly so every new table gets the house style
 * for free. All DataGrid props are forwarded; any per-page `sx` is merged ON
 * TOP of `dataGridSx` (so callers can still tweak individual selectors), and
 * the defaults above can be overridden by passing the prop explicitly.
 */
export function WafDataGrid<R extends GridValidRowModel>(props: DataGridProps<R>) {
  const { sx, slots, ...rest } = props;
  // Merge dataGridSx first, then the caller's sx, so caller selectors win.
  // dataGridSx is a function form; SxProps accepts an array of entries.
  const mergedSx: SxProps<Theme> = sx
    ? [dataGridSx, ...(Array.isArray(sx) ? sx : [sx])]
    : dataGridSx;

  // Default to the branded loading overlay, but let callers override via slots.
  const mergedSlots: DataGridProps<R>['slots'] = {
    loadingOverlay: WafLoadingOverlay,
    ...slots,
  };

  // Wrap in a min-width:0 / max-width:100% box so the grid stays inside its
  // (often flex/grid) parent on narrow screens. Without min-width:0 a flex
  // child won't shrink below its content width, which would push the grid's
  // internal horizontal scroll off-screen on mobile instead of scrolling
  // within the grid. The DataGrid handles its own column overflow.
  return (
    <Box sx={{ width: '100%', minWidth: 0, maxWidth: '100%' }}>
      <DataGrid<R>
        density="compact"
        disableRowSelectionOnClick
        {...rest}
        slots={mergedSlots}
        sx={mergedSx}
      />
    </Box>
  );
}
